'use client';

import React from 'react';
import Link from 'next/link';
import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';
import { useTranslation } from '@/hooks/useTranslation';

interface Manufacturer {
  id: number;
  name: string;
  country: string;
  specialization: string;
  certifications: string[];
  productCount: number;
  responseRate: number;
  image: string;
  alt: string;
  verified: boolean;
}

const FeaturedManufacturers = () => {
  const { t } = useTranslation();
  
  const manufacturers: Manufacturer[] = [
  {
    id: 1,
    name: 'Precision Engineering Ltd',
    country: 'Germany',
    specialization: 'CNC Machining & Metal Fabrication',
    certifications: ['ISO 9001', 'ISO 14001'],
    productCount: 342,
    responseRate: 98,
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_15e40dd6e-1764703106290.png",
    alt: 'Modern industrial factory floor with CNC machines and metal fabrication equipment',
    verified: true
  },
  {
    id: 2,
    name: 'Global Electronics Manufacturing',
    country: 'China',
    specialization: 'PCB Assembly & Electronic Components',
    certifications: ['ISO 9001', 'RoHS', 'CE'],
    productCount: 1247,
    responseRate: 95,
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1ae1b3938-1767521812037.png",
    alt: 'Electronics manufacturing facility with circuit board assembly lines and testing equipment',
    verified: true
  },
  {
    id: 3,
    name: 'Advanced Plastics Corp',
    country: 'United States',
    specialization: 'Injection Molding & Polymer Solutions',
    certifications: ['ISO 9001', 'FDA Approved'],
    productCount: 589,
    responseRate: 97,
    image: "https://images.unsplash.com/photo-1730705788367-dbd288c40ee7",
    alt: 'Plastic injection molding factory with automated machinery and quality control stations',
    verified: true
  },
  {
    id: 4,
    name: 'Industrial Textiles Group',
    country: 'India',
    specialization: 'Technical Fabrics & Composite Materials',
    certifications: ['ISO 9001', 'OEKO-TEX'],
    productCount: 423,
    responseRate: 93,
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_185cdd612-1766522161264.png",
    alt: 'Textile manufacturing plant with industrial looms and fabric production machinery',
    verified: true
  }];


  return (
    <section className="py-20 px-6 bg-background">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-12">
          <h2 className="text-4xl font-headline font-bold text-foreground mb-4">
            {t('featuredManufacturers')}
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            {t('featuredManufacturersDesc')}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {manufacturers.map((manufacturer) =>
          <Link
            key={manufacturer.id}
            href="/manufacturer-directory"
            className="bg-card border border-border rounded-lg overflow-hidden hover:shadow-lg transition-shadow duration-200 group">

              <div className="relative h-48 overflow-hidden bg-muted">
                <AppImage
                src={manufacturer.image}
                alt={manufacturer.alt}
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />

                {manufacturer.verified &&
              <div className="absolute top-3 right-3 bg-verified text-verified-foreground px-3 py-1 rounded-full text-xs font-semibold flex items-center gap-1">
                    <Icon name="CheckBadgeIcon" size={14} variant="solid" />
                    {t('verified')}
                  </div>
              }
              </div>

              <div className="p-5">
                <h3 className="text-lg font-headline font-bold text-foreground mb-2 line-clamp-1">
                  {manufacturer.name}
                </h3>
                <div className="flex items-center gap-2 text-sm text-muted-foreground mb-3">
                  <Icon name="MapPinIcon" size={16} variant="outline" />
                  <span>{manufacturer.country}</span>
                </div>

                <p className="text-sm text-muted-foreground mb-4 line-clamp-2">
                  {manufacturer.specialization}
                </p>

                <div className="flex flex-wrap gap-2 mb-4">
                  {manufacturer.certifications.slice(0, 2).map((cert, index) =>
                <span
                  key={index}
                  className="px-2 py-1 bg-muted text-foreground text-xs rounded border border-border">

                      {cert}
                    </span>
                )}
                </div>

                <div className="flex items-center justify-between text-sm pt-4 border-t border-border">
                  <div className="flex items-center gap-1 text-muted-foreground">
                    <Icon name="CubeIcon" size={16} variant="outline" />
                    <span>{manufacturer.productCount} {t('productsCount')}</span>
                  </div>
                  <div className="flex items-center gap-1 text-success">
                    <Icon name="CheckCircleIcon" size={16} variant="solid" />
                    <span>{manufacturer.responseRate}%</span>
                  </div>
                </div>
              </div>
            </Link>
          )}
        </div>

        <div className="text-center mt-10">
          <Link
            href="/manufacturer-directory"
            className="inline-flex items-center gap-2 px-6 py-3 bg-primary text-primary-foreground rounded-md font-headline font-semibold hover:bg-primary/90 transition-colors duration-200">

            {t('viewAllManufacturers')}
            <Icon name="ArrowRightIcon" size={20} variant="outline" />
          </Link>
        </div>
      </div>
    </section>);

};

export default FeaturedManufacturers;